// An IR blaster in the desktop room grid / common strip.
//
// Shortcuts, not a remote. The TV pad alone is 33 keys and belongs on the device
// screen; what a grid card is for is the two or three things you actually reach
// for. "Open remote" goes to the full pads.
//
// It replaces GenericControlCard for these devices, which fetched their outputs,
// found none, and drew "No controllable outputs" over a grey icon — true, and
// useless, about a device with eighty working keys.

import React, { useState } from 'react';
import {
  Card, Box, Typography, Button, Tooltip,
} from '@mui/material';
import SettingsRemoteIcon from '@mui/icons-material/SettingsRemote';
import { AC_KEYS, TV_KEYS, LIGHT_KEYS } from '../../../core/devices/irRemote';
import { useIrRemote } from '../../../core/hooks/useIrRemote';
import { CARD_TYPE } from '../../../core/devices/buttonLayout';
import RemoteHandset from '../../shared/remote/RemoteHandset';
import DeviceStatusDot from './DeviceStatusDot';
import DeviceCardMenu from './DeviceCardMenu';

// Deliberately short. Every entry is a token from core/devices/irRemote — no
// string is written here.
const SHORTCUTS = [
  { label: 'TV Power', token: TV_KEYS.power },
  { label: 'TV Mute', token: TV_KEYS.mute },
  { label: 'AC On', token: AC_KEYS.on },
  { label: 'AC Off', token: AC_KEYS.off },
  { label: 'Lights On', token: LIGHT_KEYS.allOn },
  { label: 'Lights Off', token: LIGHT_KEYS.allOff },
];

export default function IrRemoteControlCard({ device, subtitle }) {
  const uno = device.UniqueNumber;
  // Same hook as the pads, so the double-blast lock covers these keys too.
  const { send, lastSent, liveAt } = useIrRemote(uno, device.Status || '');
  // The full handset opens HERE, over the room grid, rather than navigating to
  // the device page. Picking up a remote should not cost you the screen you were
  // on — and it means the remote is reachable from anywhere the card appears.
  const [open, setOpen] = useState(false);

  return (
    <Card sx={{
      p: 1.25,
      borderRadius: 3,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 0.75,
      bgcolor: 'background.paper',
      border: 1,
      borderColor: 'divider',
    }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <SettingsRemoteIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {subtitle && (
            <Typography color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.2, fontSize: CARD_TYPE.subtitle }}>
              {subtitle}
            </Typography>
          )}
          <Typography fontWeight={700} color="text.primary" noWrap sx={{ fontSize: CARD_TYPE.title }}>
            {device.DeviceName || uno}
          </Typography>
        </Box>
        <DeviceStatusDot device={device} live={liveAt} />
        <DeviceCardMenu device={device} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
        {SHORTCUTS.map((s) => (
          <Tooltip key={s.label} title={s.token}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => send(s.token)}
              sx={{
                fontSize: CARD_TYPE.channel, textTransform: 'none', py: 0.25, px: 0.5, minWidth: 0,
              }}
            >
              {s.label}
            </Button>
          </Tooltip>
        ))}
      </Box>

      <Button
        size="small"
        variant={open ? 'outlined' : 'contained'}
        startIcon={<SettingsRemoteIcon sx={{ fontSize: 14 }} />}
        onClick={() => setOpen((v) => !v)}
        sx={{ fontSize: CARD_TYPE.action, py: 0.25, mt: 'auto' }}
      >
        {open ? 'Close remote' : 'Open remote'}
      </Button>

      {/* What was SENT. The blaster cannot tell us the appliance responded —
          see the open-loop note in core/hooks/useIrRemote. */}
      <Typography sx={{ fontSize: CARD_TYPE.state, color: 'text.secondary', minHeight: 14 }}>
        {lastSent ? `Sent ${lastSent.token}` : ''}
      </Typography>

      {open && (
        <RemoteHandset
          floating
          uniqueNumber={uno}
          deviceName={device.DeviceName || uno}
          initialStatus={device.Status || ''}
          onClose={() => setOpen(false)}
        />
      )}
    </Card>
  );
}
